// A PHOTO IS REDRAWN BEFORE IT LEAVES THE BROWSER, ALWAYS (WEB-PARITY-3, CD-4) — the app's `PhotoStorage.save`
// (AppealAce/Services/PhotoStorage.swift:19-29, 39-58): the long edge capped at 1,600 pixels, a JPEG at quality 0.75.
//
// PRIVACY — LOAD-BEARING, AND THE APP SAYS SO IN CAPITALS (:41-47): redrawing produces a fresh bitmap with NO metadata,
// which strips the original photo's EXIF — GPS location included — before it is ever uploaded. So there is no "skip the
// redraw for an already-small photo" path here either: a small photo would keep its coordinates. `test/parity3.test.tsx`
// pins that a photo already under the cap is still redrawn.
//
// The redraw is behind a seam (`PhotoCodec`) because jsdom has no canvas; the browser's is `browserCodec`.

/** `PhotoStorage.downscaled(_:maxDimension:)`'s 1600 (:20). */
export const PHOTO_MAX_DIMENSION = 1600;
/** `jpegData(compressionQuality:)`'s 0.75 (:21). */
export const PHOTO_JPEG_QUALITY = 0.75;

export interface PreparedUpload {
  /** The JPEG's bytes, base64, as `op:"evidence"` takes them. */
  readonly base64: string;
  readonly contentType: "image/jpeg";
}

export interface DecodedImage {
  readonly width: number;
  readonly height: number;
}

/** Decode a picked file, then draw it at a size and encode the drawing as a JPEG — the two halves of the redraw. */
export interface PhotoCodec<I extends DecodedImage = DecodedImage> {
  decode(file: Blob): Promise<I>;
  /** Draws `image` into a fresh `width` × `height` bitmap and answers that bitmap as JPEG bytes, base64. */
  redraw(image: I, width: number, height: number, quality: number): Promise<string>;
}

/** The size the redraw draws at: the long edge capped, never enlarged, whole pixels (`downscaled`, :48-52). */
export function redrawSize(width: number, height: number): { width: number; height: number } {
  const longest = Math.max(width, height, 1);
  const scale = Math.min(1, PHOTO_MAX_DIMENSION / longest);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export async function preparePhoto<I extends DecodedImage>(file: Blob, codec: PhotoCodec<I>): Promise<PreparedUpload> {
  const image = await codec.decode(file);
  const size = redrawSize(image.width, image.height);
  // EVERY photo is redrawn — see this file's header.
  const base64 = await codec.redraw(image, size.width, size.height, PHOTO_JPEG_QUALITY);
  return { base64, contentType: "image/jpeg" };
}

interface BitmapImage extends DecodedImage {
  readonly bitmap: ImageBitmap;
}

/** The browser's codec: `createImageBitmap` (which applies the photo's own orientation) and a canvas. */
export const browserCodec: PhotoCodec<BitmapImage> = {
  async decode(file) {
    const bitmap = await createImageBitmap(file);
    return { bitmap, width: bitmap.width, height: bitmap.height };
  },
  async redraw(image, width, height, quality) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("no 2d canvas");
    context.drawImage(image.bitmap, 0, 0, width, height);
    image.bitmap.close();
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b === null ? reject(new Error("the photo could not be encoded")) : resolve(b)), "image/jpeg", quality);
    });
    return base64Of(blob);
  },
};

/** A blob's bytes as base64, without the `data:` prefix. */
export async function base64Of(blob: Blob): Promise<string> {
  const url = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("the photo could not be read"));
    reader.readAsDataURL(blob);
  });
  return url.slice(url.indexOf(",") + 1);
}
